import { Check, ChevronDown, Search, X } from "lucide-react";
import {
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

export interface DropdownOption {
  value: string;
  label: string;
  description?: string;
  /** Optional icon rendered next to the label (e.g. <Shield className="w-4 h-4" />). */
  icon?: ReactNode;
  disabled?: boolean;
}

export interface DropdownProps {
  options: DropdownOption[];
  value: string | null | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Show a search input inside the panel. Auto-enabled when options.length >= 8. */
  searchable?: boolean;
  /** Show an X to clear the selection. */
  clearable?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  /** Tailwind width on the trigger button (default `w-64`). */
  className?: string;
  /** sm = py-1.5 text-xs, md = py-2 text-sm (default), lg = py-2.5 text-base */
  size?: "sm" | "md" | "lg";
  /** Text shown inside an empty options panel. */
  emptyText?: string;
  /** Custom render for the selected pill in the trigger. */
  renderSelected?: (option: DropdownOption) => ReactNode;
  ariaLabel?: string;
  id?: string;
  name?: string;
}

const sizeMap = {
  sm: "py-1.5 text-xs",
  md: "py-2 text-sm",
  lg: "py-2.5 text-base",
} as const;

/**
 * Headless, fully custom dropdown — no native <select>.
 *
 * Features:
 *   - Click-outside / Escape to close.
 *   - Keyboard nav: ArrowUp/Down moves highlight, Enter selects, Home/End jump.
 *   - Optional searchable filter inside the panel (auto-enabled at 8+ options).
 *   - Optional clear button on the trigger.
 *   - Proper ARIA combobox roles + aria-activedescendant.
 *   - Auto-flips upward if there isn't enough room below.
 */
function Dropdown({
  options,
  value,
  onChange,
  placeholder = "Select…",
  searchable,
  clearable,
  onClear,
  disabled,
  className = "w-64",
  size = "md",
  emptyText = "No options",
  renderSelected,
  ariaLabel,
  id,
  name,
}: DropdownProps) {
  const reactId = useId();
  const listboxId = id ?? `dd-${reactId}`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState<number>(-1);
  const [flipUp, setFlipUp] = useState(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  const isSearchable = searchable ?? options.length >= 8;

  const filtered = useMemo(() => {
    if (!isSearchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => {
      return (
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.description ?? "").toLowerCase().includes(q)
      );
    });
  }, [options, query, isSearchable]);

  const closePanel = useCallback(() => {
    setOpen(false);
    setQuery("");
    setHighlight(-1);
  }, []);

  // Click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) closePanel();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, closePanel]);

  // Decide flip direction when opening
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setFlipUp(spaceBelow < 280 && spaceAbove > spaceBelow);
  }, [open]);

  // When opening, focus search input or set initial highlight
  useEffect(() => {
    if (!open) return;
    if (isSearchable) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
    const initial = filtered.findIndex((o) => o.value === value);
    setHighlight(initial >= 0 ? initial : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep highlight in sync when filter changes
  useEffect(() => {
    if (!open) return;
    setHighlight((h) => (filtered.length === 0 ? -1 : Math.max(0, Math.min(h, filtered.length - 1))));
  }, [filtered, open]);

  // Scroll highlighted into view
  useEffect(() => {
    if (!open || highlight < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLLIElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const selectAt = (idx: number) => {
    const opt = filtered[idx];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    closePanel();
    triggerRef.current?.focus();
  };

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if ((e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") && !open) {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      closePanel();
    }
  };

  const onPanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closePanel();
      triggerRef.current?.focus();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => {
        if (filtered.length === 0) return -1;
        let n = (h + 1) % filtered.length;
        // skip disabled
        let safety = filtered.length;
        while (filtered[n]?.disabled && safety-- > 0) n = (n + 1) % filtered.length;
        return n;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => {
        if (filtered.length === 0) return -1;
        let n = (h - 1 + filtered.length) % filtered.length;
        let safety = filtered.length;
        while (filtered[n]?.disabled && safety-- > 0) n = (n - 1 + filtered.length) % filtered.length;
        return n;
      });
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlight(filtered.length - 1);
    } else if (e.key === "Enter") {
      if (highlight >= 0) {
        e.preventDefault();
        selectAt(highlight);
      }
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear ? onClear() : onChange("");
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`} onKeyDown={onPanelKeyDown}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        id={listboxId}
        name={name}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${listboxId}-list`}
        aria-label={ariaLabel ?? placeholder}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        className={[
          "w-full flex items-center justify-between gap-2 pl-3 pr-2 border rounded-md bg-white text-left",
          "border-gray-300 hover:border-gray-400",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          "disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed",
          "transition-colors",
          sizeMap[size],
        ].join(" ")}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          {selected ? (
            renderSelected ? (
              renderSelected(selected)
            ) : (
              <>
                {selected.icon && <span className="shrink-0 text-gray-500">{selected.icon}</span>}
                <span className="truncate text-gray-900">{selected.label}</span>
              </>
            )
          ) : (
            <span className="truncate text-gray-400">{placeholder}</span>
          )}
        </span>

        <span className="flex items-center gap-1 shrink-0">
          {clearable && selected && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </span>
      </button>

      {/* Panel */}
      {open && (
        <div
          className={[
            "absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg",
            "ring-1 ring-black/5 overflow-hidden",
            flipUp ? "bottom-full mb-1" : "top-full mt-1",
            "animate-[fadeIn_120ms_ease-out]",
          ].join(" ")}
        >
          {isSearchable && (
            <div className="p-2 border-b border-gray-100 bg-gray-50">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          <ul
            ref={listRef}
            id={`${listboxId}-list`}
            role="listbox"
            aria-activedescendant={highlight >= 0 ? `${listboxId}-opt-${highlight}` : undefined}
            tabIndex={-1}
            className="max-h-64 overflow-y-auto py-1"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-gray-500">{emptyText}</li>
            ) : (
              filtered.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isHighlighted = idx === highlight;
                return (
                  <li
                    id={`${listboxId}-opt-${idx}`}
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled || undefined}
                    data-idx={idx}
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectAt(idx)}
                    className={[
                      "flex items-start gap-2 px-3 py-2 cursor-pointer text-sm",
                      opt.disabled
                        ? "text-gray-400 cursor-not-allowed"
                        : isHighlighted
                        ? "bg-blue-50 text-blue-900"
                        : "text-gray-700 hover:bg-gray-50",
                    ].join(" ")}
                  >
                    {opt.icon && <span className="shrink-0 mt-0.5 text-gray-500">{opt.icon}</span>}
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium">{opt.label}</div>
                      {opt.description && (
                        <div className="text-xs text-gray-500 truncate">{opt.description}</div>
                      )}
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default Dropdown;
